import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "关于", to: "/about" },
  { label: "常见问题", to: "/faq" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-gradient-to-b from-foreground-950/70 to-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-film-line text-xl md:text-2xl text-accent-400"></i>
            </span>
            <span
              className={`text-lg md:text-xl font-bold tracking-tight font-heading ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              光棍影院
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? "text-accent-500"
                    : transparent
                    ? "text-background-100 hover:text-accent-400"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className={`hidden sm:flex items-center gap-2 rounded-full px-3.5 py-2 w-44 lg:w-64 transition-colors ${
                transparent
                  ? "bg-background-50/20 border border-background-50/30"
                  : "bg-background-100 border border-background-200"
              }`}
            >
              <i
                className={`ri-search-line text-base ${
                  transparent ? "text-background-100" : "text-foreground-500"
                }`}
              ></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、主演、导演"
                className={`w-full bg-transparent text-sm outline-none ${
                  transparent
                    ? "text-background-50 placeholder-background-200"
                    : "text-foreground-900 placeholder-foreground-400"
                }`}
              />
            </form>

            <Link
              to="/search"
              className={`sm:hidden w-9 h-9 flex items-center justify-center rounded-full ${
                transparent ? "text-background-50" : "text-foreground-800"
              }`}
            >
              <i className="ri-search-line text-xl"></i>
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${
                transparent ? "text-background-50" : "text-foreground-900"
              }`}
            >
              <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <div className="px-4 py-3 flex flex-col gap-1">
            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2 rounded-full px-3.5 py-2 bg-background-100 border border-background-200 mb-2"
            >
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、主演、导演"
                className="w-full bg-transparent text-sm outline-none text-foreground-900 placeholder-foreground-400"
              />
            </form>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2.5 rounded-md text-sm font-medium ${
                  isActive(link.to)
                    ? "text-accent-500 bg-accent-50"
                    : "text-foreground-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}